export const SPACING_SCALE = {
  0: "0px",
  1: "8px",
  2: "16px",
  3: "24px",
  4: "32px",
  5: "40px",
  6: "48px",
  7: "56px",
  8: "64px",
  9: "72px",
  10: "80px",
  11: "88px",
  12: "96px",
  13: "104px",
  14: "112px",
  15: "120px",
} as const;

export type SpacingScaleStep = keyof typeof SPACING_SCALE;

export const TYPE_SCALE = {
  xs: "12px",
  sm: "15px",
  base: "16px",
  md: "20px",
  lg: "25px",
  xl: "31px",
  "2xl": "39px",
  "3xl": "49px",
} as const;

export const SIMPLE_EDITOR_TYPE_SCALE = {
  body: "16px",
  h3: "24px",
  h2: "32px",
  h1: "48px",
} as const;

export const SIMPLE_EDITOR_DEFAULTS = {
  sectionSpacing: "120px",
  sectionGap: "0px",
  buttonRadius: "6px",
  cardRadius: "8px",
  cardBackground: "#FFFFFF",
  cardShadow: "0 12px 30px rgba(15,23,42,.10)",
  buttonTextColor: "#FFFFFF",
} as const;

export type TypeScaleStep = keyof typeof TYPE_SCALE;

export interface SmartFontPair {
  id: string;
  label: string;
  heading: {
    family: string;
    googleFont: string;
    weight: string;
  };
  body: {
    family: string;
    googleFont: string;
    weight: string;
  };
}

export const CONTENT_WIDTHS = {
  xs: "500px",
  sm: "600px",
  md: "720px",
  lg: "800px",
  xl: "1000px",
  wide: "1200px",
} as const;

export const CONTENT_WIDTH_OPTION_VALUES = {
  sm: "640",
  md: "800",
  lg: "1024",
  full: "100%",
} as const;

export const SPACING_OPTION_VALUES = {
  none: "0",
  xs: "8",
  sm: "16",
  stack: "24",
  md: "32",
  section: "48",
  lg: "64",
  xl: "96",
  heroY: "120",
} as const;

export const FONT_SIZE_OPTION_VALUES = {
  sm: TYPE_SCALE.sm,
  md: TYPE_SCALE.base,
  lg: TYPE_SCALE.md,
  xl: TYPE_SCALE.lg,
} as const;

export const CURATED_FONT_PAIRS: readonly SmartFontPair[] = [
  {
    id: "modern-sans",
    label: "Modern Sans",
    heading: { family: "Inter", googleFont: "Inter", weight: "700" },
    body: { family: "Inter", googleFont: "Inter", weight: "400" },
  },
  {
    id: "editorial",
    label: "Editorial",
    heading: { family: "Playfair Display", googleFont: "Playfair Display", weight: "700" },
    body: { family: "Source Sans 3", googleFont: "Source Sans 3", weight: "400" },
  },
  {
    id: "campaign",
    label: "Campaign",
    heading: { family: "Montserrat", googleFont: "Montserrat", weight: "800" },
    body: { family: "Montserrat", googleFont: "Montserrat", weight: "500" },
  },
  {
    id: "quiet",
    label: "Quiet Sans",
    heading: { family: "DM Sans", googleFont: "DM Sans", weight: "700" },
    body: { family: "DM Sans", googleFont: "DM Sans", weight: "400" },
  },
  {
    id: "friendly",
    label: "Friendly Rounded",
    heading: { family: "Poppins", googleFont: "Poppins", weight: "700" },
    body: { family: "Poppins", googleFont: "Poppins", weight: "400" },
  },
] as const;

export function spacing(step: SpacingScaleStep | number): string {
  return SPACING_SCALE[step as SpacingScaleStep] ?? `${Number(step) * 8}px`;
}

export function fontSize(step: TypeScaleStep): string {
  return TYPE_SCALE[step];
}

export function spacingValue(step: SpacingScaleStep): string {
  return SPACING_SCALE[step].replace("px", "");
}
